/**
 * Stage 4 client calls. Query keys: ['lead', id, 'screening'] and
 * ['settings', 'screening']. The lead screening query polls every 10 s while
 * the latest call is scheduled / dialing / in progress.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api-client';
import { LEADS_KEY, leadKey } from '@/features/pipeline/api';
import { normalizeConfig, normalizeConfigs } from './normalize';
import { isActiveScreening, type LeadScreening, type ScreeningCall, type ScreeningConfig, type ScreeningConfigs } from './types';

export const SCREENING_POLL_MS = 10_000;
export const leadScreeningKey = (id: string) => [...leadKey(id), 'screening'] as const;
export const SCREENING_SETTINGS_KEY = ['settings', 'screening'] as const;

function callOf(raw: unknown): ScreeningCall | null {
  if (!raw || typeof raw !== 'object') return null;
  const c = raw as Partial<ScreeningCall> & { extracted?: Record<string, unknown> | null };
  if (!c.id || !c.status) return null;
  return {
    id: c.id,
    attempt_no: typeof c.attempt_no === 'number' ? c.attempt_no : 1,
    status: c.status,
    scheduled_for: c.scheduled_for ?? null,
    started_at: c.started_at ?? null,
    ended_at: c.ended_at ?? null,
    duration_s: typeof c.duration_s === 'number' ? c.duration_s : null,
    disconnect_reason: c.disconnect_reason ?? null,
    transcript: c.transcript ?? null,
    recording_url: c.recording_url ?? null,
    extracted_json: c.extracted_json ?? c.extracted ?? null,
    qualification: c.qualification ?? null,
    qualification_reason: c.qualification_reason ?? null,
    created_at: c.created_at ?? c.scheduled_for ?? '',
  };
}

export function normalizeLeadScreening(raw: unknown): LeadScreening {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<LeadScreening> & { calls?: unknown[] };
  const attempts = (Array.isArray(r.attempts) ? r.attempts : Array.isArray(r.calls) ? r.calls : [])
    .map(callOf)
    .filter((c): c is ScreeningCall => !!c)
    .sort((a, b) => a.attempt_no - b.attempt_no);
  const latest = callOf(r.latest) ?? attempts[attempts.length - 1] ?? null;
  return { config_draft: r.config_draft === true, attempts, latest };
}

export function useLeadScreening(leadId: string | null) {
  const client = useApiClient();
  return useQuery({
    queryKey: leadScreeningKey(leadId ?? ''),
    queryFn: async () => normalizeLeadScreening(await client.get<unknown>(`/api/leads/${leadId}/screening`)),
    enabled: !!leadId,
    refetchInterval: (query) => (isActiveScreening(query.state.data?.latest?.status) ? SCREENING_POLL_MS : false),
  });
}

/** "Call now" + "Re-run qualification"; both refetch the screening, the lead and the board. */
export function useScreeningActions(leadId: string) {
  const client = useApiClient();
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: leadScreeningKey(leadId) });
    void qc.invalidateQueries({ queryKey: leadKey(leadId), exact: true });
    void qc.invalidateQueries({ queryKey: LEADS_KEY });
  };
  const call = useMutation({
    mutationFn: () => client.post<unknown>(`/api/leads/${leadId}/screening/call`),
    onSuccess: refresh,
  });
  const requalify = useMutation({
    mutationFn: () => client.post<unknown>(`/api/leads/${leadId}/screening/requalify`),
    onSuccess: refresh,
  });
  return { call, requalify };
}

export function useScreeningConfigs() {
  const client = useApiClient();
  return useQuery({
    queryKey: SCREENING_SETTINGS_KEY,
    queryFn: async () => normalizeConfigs(await client.get<unknown>('/api/settings/screening')),
    staleTime: 30 * 1000,
  });
}

/** `PUT /api/settings/screening/:role_slug` (company_admin). Saving clears `draft`. */
export function useSaveScreeningConfig() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleSlug, config }: { roleSlug: string; config: ScreeningConfig }) =>
      client.post<unknown>(`/api/settings/screening/${encodeURIComponent(roleSlug)}`, config, { method: 'PUT' }),
    onSuccess: (data, { roleSlug, config }) => {
      const echoed = data && typeof data === 'object' ? normalizeConfig(data) : null;
      const saved = echoed && echoed.questions.length ? echoed : config;
      qc.setQueryData<ScreeningConfigs>(SCREENING_SETTINGS_KEY, (prev) => ({ ...(prev ?? {}), [roleSlug]: saved }));
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}
